const STORAGE_KEY = 'hivecloud:user-auth-return-path'
let memoryReturnPath: string | undefined
const accountAnchors = new Set(['profile', 'devices', 'login-devices', 'sessions', 'mfa', 'security', 'recovery', 'lifecycle', 'delete-account'])
// eslint-disable-next-line no-control-regex -- Reject control bytes at the navigation trust boundary.
const controlCharacters = /[\u0000-\u001f\u007f]/

export function safeReturnPath(value: unknown): string | undefined {
  if (typeof value !== 'string' || value.length > 2048 || value.trim() !== value
    || !value.startsWith('/') || value.startsWith('//') || value.includes('\\') || controlCharacters.test(value)) return undefined
  try {
    // Validate even parameters which will be removed, rather than accepting
    // malformed encodings and letting a later layer interpret them differently.
    decodeURIComponent(value)
    const base = typeof window === 'undefined' ? 'https://hive.invalid' : window.location.origin
    const url = new URL(value, base)
    if (url.origin !== base || url.username || url.password) return undefined
    const rawPath = value.split(/[?#]/, 1)[0]
    let decodedPath = rawPath
    for (let index = 0; index < 4 && decodedPath.includes('%'); index += 1) {
      decodedPath = decodeURIComponent(decodedPath)
    }
    if (decodedPath.includes('%') || decodedPath.startsWith('//') || decodedPath.includes('\\')
      || decodedPath.includes('?') || decodedPath.includes('#') || controlCharacters.test(decodedPath)
      || decodedPath.split('/').some((part) => part === '.' || part === '..')) return undefined
    // Encoded separators must not create a second route interpretation.
    if (decodedPath.split('/').length !== rawPath.split('/').length) return undefined
    const runtimeReturn = decodedPath === '/runtime/' || decodedPath === '/runtime'
    const runtimeId = url.searchParams.get('runtime')
    if (runtimeReturn && (url.searchParams.getAll('runtime').length !== 1 || !runtimeId
      || !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(runtimeId))) return undefined
    const allowed = runtimeReturn || /^\/(?:overview|runtimes(?:\/[A-Za-z0-9._-]{1,256})?|work(?:\/[A-Za-z0-9._-]{1,256})?|account|help(?:\/[^/]+)?|runtime-claim|devices|sessions|legal|support)$/.test(decodedPath)
    if (!allowed) return undefined
    const query = new URLSearchParams()
    if (runtimeReturn) query.set('runtime', runtimeId!)
    const locale = url.searchParams.get('locale')
    if (locale && /^(?:zh-CN|zh|zh-Hans|en-US|en)$/.test(locale)) query.set('locale', locale)
    if (decodedPath === '/account') {
      const device = url.searchParams.get('device')
      if (device && /^[A-Za-z0-9._-]{1,256}$/.test(device)) query.set('device', device)
    }
    if (decodedPath === '/runtimes' && url.searchParams.get('claim') === '1') query.set('claim', '1')
    if (decodedPath === '/runtimes') {
      const q = url.searchParams.get('q')
      const filter = url.searchParams.get('filter')
      const sort = url.searchParams.get('sort')
      if (q && q.length <= 120 && !controlCharacters.test(q)) query.set('q', q)
      if (filter && ['all', 'online', 'attention', 'offline'].includes(filter)) query.set('filter', filter)
      if (sort && ['recent', 'name', 'status'].includes(sort)) query.set('sort', sort)
    }
    let hash = ''
    const anchor = decodeURIComponent(url.hash.slice(1))
    if (decodedPath === '/account' && accountAnchors.has(anchor)) hash = `#${anchor}`
    else if (decodedPath === '/legal' && (anchor === 'terms' || anchor === 'privacy')) hash = `#${anchor}`
    else if (decodedPath.startsWith('/help/') && /^[A-Za-z0-9_-]{1,100}$/.test(anchor)) hash = `#${anchor}`
    let path = runtimeReturn ? '/runtime/' : url.pathname
    if (decodedPath === '/devices') { path = '/account'; hash = '#devices' }
    if (decodedPath === '/sessions') { path = '/account'; hash = '#sessions' }
    const search = query.toString()
    return path + (search ? `?${search}` : '') + hash
  } catch {
    return undefined
  }
}

export const requiresDocumentNavigation = (path: string): boolean => path.startsWith('/runtime/')

export function storeAuthenticationReturnPath(value?: string): void {
  memoryReturnPath = safeReturnPath(value)
  if (typeof sessionStorage === 'undefined') return
  try {
    if (memoryReturnPath) sessionStorage.setItem(STORAGE_KEY, memoryReturnPath)
    else sessionStorage.removeItem(STORAGE_KEY)
  } catch { /* The same-document continuation remains available in memory. */ }
}

export function clearAuthenticationReturnPath(): void { storeAuthenticationReturnPath() }

export function consumeAuthenticationReturnPath(fallback = '/overview'): string {
  let value = memoryReturnPath
  if (typeof sessionStorage !== 'undefined') {
    try { value = sessionStorage.getItem(STORAGE_KEY) ?? value; sessionStorage.removeItem(STORAGE_KEY) } catch { /* Use memory. */ }
  }
  memoryReturnPath = undefined
  return safeReturnPath(value) ?? safeReturnPath(fallback) ?? '/overview'
}
